import { RefObject, useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
  /** Called when the sentinel scrolls into view (and loading is enabled). */
  onLoadMore: () => void;
  /** Observe only when there's another page AND we're not already loading one. */
  enabled: boolean;
  /** The scroll container the sentinel lives in — the observer root. Defaults to the viewport. */
  root?: RefObject<HTMLElement | null>;
  /** Prefetch margin so the next page starts before the user hits the exact bottom. */
  rootMargin?: string;
}

/**
 * Returns a ref to attach to a sentinel element at the bottom of a list; when that sentinel enters the
 * root's viewport (within `rootMargin`) and `enabled` is true, `onLoadMore` fires. Pass a stable
 * `onLoadMore` (useCallback) — the observer re-attaches when it, `enabled`, or the root changes.
 */
export const useInfiniteScroll = ({
  onLoadMore,
  enabled,
  root,
  rootMargin = '240px',
}: UseInfiniteScrollOptions): RefObject<HTMLDivElement> => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !enabled) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { root: root?.current ?? null, rootMargin },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, enabled, root, rootMargin]);

  return sentinelRef;
};
